import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Panel from '../../components/Panel/Panel';
import { useAppSelector } from '../../hooks';
import { Scene } from '../../enums';
import { mailRequest } from '../Mail/Mailbox';
import './PocketSocial.css';

interface Person {
  id: number;
  name: string;
  level: number;
  avatar: number;
  photo: string;
  bio: string;
}
interface Profile extends Person {
  followers: number;
  following: number;
  friends: number;
  followed: boolean;
  friendship: string;
}
interface Post extends Person {
  my_reaction?: string | null;
  reactions?: Record<string, number>;
  post_id: string;
  author: number;
  body: string;
  image: string;
  feeling: string;
  created_at: string;
  comments: number;
}
interface Comment extends Person {
  comment_id: string;
  body: string;
  created_at: string;
}
interface Story extends Person {
  story_id: string;
  author: number;
  image: string;
  body: string;
  created_at: string;
}
const request = <T,>(action: string, data: Record<string, unknown> = {}) => mailRequest<T>('pocketSocial', { action, ...data });
const feelings = ['happy', 'excited', 'love', 'sad', 'tired'] as const;
const emoji: Record<string, string> = { happy: '😊', excited: '🤩', love: '❤️', sad: '😢', tired: '😴' };
const reactionIcons: Record<string, string> = { like: '👍', love: '❤️', care: '🥰', haha: '😆', wow: '😮', sad: '😢', angry: '😡' };
export async function prepareSocialImage(file: File, avatar = false): Promise<string> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) throw new Error('social_error_image');
  const source = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('social_error_image'));
      img.src = source;
    });
    const canvas = document.createElement('canvas'),
      max = avatar ? 320 : 1200;
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('social_error_image');
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    let data = canvas.toDataURL('image/jpeg', 0.82);
    if (data.length > 550000) data = canvas.toDataURL('image/jpeg', 0.5);
    if (data.length > 550000) throw new Error('social_error_image');
    return data;
  } finally {
    URL.revokeObjectURL(source);
  }
}
function Portrait({ person, onClick }: { person: Person; onClick?: () => void }) {
  const { t } = useTranslation();
  return (
    <button className="ps-person" onClick={onClick} type="button">
      <img src={person.photo || `${process.env.REACT_APP_CDN_PATH ?? ''}icons/people/${person.avatar}.png`} alt="" />
      <span>
        <b>{person.name}</b>
        <small>
          {t('Level')} {person.level}
        </small>
      </span>
    </button>
  );
}
export function PocketSocial() {
  const { t } = useTranslation();
  const scene = useAppSelector((s) => s.scene.scene),
    fight = useAppSelector((s) => s.fight.fight);
  const [open, setOpen] = useState(false);
  const visible =
    [Scene.FIRE_VILLAGE, Scene.WATER_VILLAGE, Scene.WIND_VILLAGE, Scene.EARTH_VILLAGE, Scene.LIGHTNING_VILLAGE].includes(scene) && !fight;
  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);
  return (
    <>
      {visible && (
        <button
          className="ps-launcher"
          title={String(t('social_title'))}
          aria-label={String(t('social_title'))}
          onClick={() => setOpen(!open)}>
          <span className="ps-icon-crop">
            <img src={`${process.env.PUBLIC_URL}/social/icon_pk.png`} alt="" />
          </span>
        </button>
      )}
      {open && <SocialWindow onClose={() => setOpen(false)} />}
    </>
  );
}
export function SocialWindow({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const self = useAppSelector((s) => s.account.id)!;
  const [me, setMe] = useState<Profile | null>(null);
  const [stories, setStories] = useState<Story[]>([]),
    [storiesMore, setStoriesMore] = useState(false);
  const [storyPhoto, setStoryPhoto] = useState(''),
    [storyBody, setStoryBody] = useState(''),
    [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const storyFile = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<'feed' | 'following' | 'profile' | 'people' | 'friends'>('feed');
  const [target, setTarget] = useState(self),
    [profile, setProfile] = useState<Profile | null>(null);
  const [posts, setPosts] = useState<Post[]>([]),
    [more, setMore] = useState(false),
    [people, setPeople] = useState<Person[]>([]);
  const [connections, setConnections] = useState<(Person & { state: string; outgoing: boolean })[]>([]);
  const [query, setQuery] = useState(''),
    [revision, setRevision] = useState(0),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const [body, setBody] = useState(''),
    [photo, setPhoto] = useState(''),
    [feeling, setFeeling] = useState('');
  const [bio, setBio] = useState(''),
    [profilePhoto, setProfilePhoto] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const composeRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    let active = true;
    request<{ stories: Story[]; more: boolean }>('stories')
      .then((result) => {
        if (active) {
          setStories(result.stories);
          setStoriesMore(result.more);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [revision]);
  useEffect(() => {
    if (!storyPhoto && !selectedStory) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        setStoryPhoto('');
        setSelectedStory(null);
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [storyPhoto, selectedStory, busy]);
  useEffect(() => {
    let active = true;
    request<Profile>('profile')
      .then((p) => {
        if (active) setMe(p);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [revision]);
  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'social_error_failed');
    } finally {
      setBusy(false);
    }
  };
  const refresh = () => setRevision((v) => v + 1);
  const view = (id: number) => {
    setTarget(id);
    setTab('profile');
    setError('');
  };
  useEffect(() => {
    let active = true;
    setError('');
    setPosts([]);
    setProfile(null);
    const timer = setTimeout(
      async () => {
        try {
          if (tab === 'profile') {
            const p = await request<Profile>('profile', { target });
            if (!active) return;
            setProfile(p);
            setBio(p.bio);
            setProfilePhoto(p.photo);
          }
          if (['feed', 'following', 'profile'].includes(tab)) {
            const feed = await request<{ posts: Post[]; more: boolean }>('feed', {
              target: tab === 'profile' ? target : undefined,
              following: tab === 'following',
            });
            if (active) {
              setPosts(feed.posts);
              setMore(feed.more);
            }
          }
          if (tab === 'people') {
            const rows = await request<Person[]>('people', { prefix: query });
            if (active) setPeople(rows);
          }
          if (tab === 'friends') {
            const rows = await request<any[]>('connections');
            if (active) setConnections(rows);
          }
        } catch (e) {
          if (active) setError(e instanceof Error ? e.message : 'social_error_failed');
        }
      },
      tab === 'people' ? 250 : 0
    );
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [tab, target, query, revision]);
  const relation = (action: string, data: Record<string, unknown>) =>
    run(async () => {
      await request(action, { target, ...data });
      refresh();
    });
  return (
    <Panel name="social_title" onClose={onClose} style={{ zIndex: 21050 }}>
      <div className="ps-window">
        <aside className="ps-sidebar">
          <div className="ps-brand">{t('social_title')}</div>
          {(['feed', 'following', 'profile', 'people', 'friends'] as const).map((key) => (
            <button
              key={key}
              className={tab === key ? 'selected' : ''}
              onClick={() => {
                setTab(key);
                if (key === 'profile') setTarget(self);
              }}>
              {t(`social_${key}`)}
            </button>
          ))}
          <button onClick={refresh}>{t('social_refresh')}</button>
        </aside>
        <div className="ps-scroll-body">
          <main className="ps-main">
            {tab === 'feed' && me && (
              <div className="ps-story-row">
                <input
                  ref={storyFile}
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  aria-label={String(t('social_story_image'))}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file)
                      run(async () => {
                        setStoryPhoto(await prepareSocialImage(file));
                        setStoryBody('');
                      });
                  }}
                />
                <button
                  className="ps-story"
                  disabled={busy}
                  aria-label={String(t('social_add_story'))}
                  onClick={() => storyFile.current?.click()}>
                  <img src={me.photo || `${process.env.REACT_APP_CDN_PATH ?? ''}icons/people/${me.avatar}.png`} alt="" />
                  <span aria-hidden="true">＋</span>
                  <small>{t('social_add_story')}</small>
                </button>
                {stories.map((story) => (
                  <button
                    key={story.story_id}
                    className="ps-story ps-story-existing"
                    aria-label={String(t('social_view_story', { name: story.name }))}
                    onClick={() => {
                      setSelectedStory(story);
                      setError('');
                    }}>
                    <img src={story.image} alt="" />
                    <small>{story.name}</small>
                  </button>
                ))}
                {storiesMore && (
                  <button
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const result = await request<{ stories: Story[]; more: boolean }>('stories', {
                          cursor: stories[stories.length - 1]?.story_id,
                        });
                        setStories((old) => [...old, ...result.stories]);
                        setStoriesMore(result.more);
                      })
                    }>
                    {t('social_more')}
                  </button>
                )}
              </div>
            )}
            {error && (
              <p className="ps-error" role="alert">
                {t(error)}
              </p>
            )}
            {tab === 'profile' && profile && (
              <section className="ps-profile">
                <Portrait person={profile} />
                <p>{profile.bio}</p>
                <div className="ps-counts">
                  {t('social_counts', { friends: profile.friends, followers: profile.followers, following: profile.following })}
                </div>
                {target === self ? (
                  <details>
                    <summary>{t('social_edit_profile')}</summary>
                    <label>
                      {t('social_bio')}
                      <textarea maxLength={300} value={bio} onChange={(e) => setBio(e.target.value)} />
                    </label>
                    <label>
                      {t('social_photo')}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        disabled={busy}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) run(async () => setProfilePhoto(await prepareSocialImage(f, true)));
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {profilePhoto && <img className="ps-photo-preview" src={profilePhoto} alt="" />}
                    <div className="ps-profile-actions">
                      <button disabled={busy} onClick={() => setProfilePhoto('')}>
                        {t('social_default_photo')}
                      </button>
                      <button
                        className="primary"
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            await request('saveProfile', { bio, photo: profilePhoto });
                            refresh();
                          })
                        }>
                        {t('social_save')}
                      </button>
                    </div>
                  </details>
                ) : (
                  <div className="ps-actions">
                    <button disabled={busy} onClick={() => relation('follow', { enabled: !profile.followed })}>
                      {t(profile.followed ? 'social_unfollow' : 'social_follow')}
                    </button>
                    {profile.friendship === 'none' && (
                      <button disabled={busy} onClick={() => relation('friend', { mode: 'request' })}>
                        {t('social_add_friend')}
                      </button>
                    )}
                    {profile.friendship === 'incoming' && (
                      <button disabled={busy} onClick={() => relation('friend', { mode: 'accept' })}>
                        {t('social_accept')}
                      </button>
                    )}
                    {profile.friendship !== 'none' && (
                      <button disabled={busy} onClick={() => relation('friend', { mode: 'remove' })}>
                        {t(
                          profile.friendship === 'accepted'
                            ? 'social_remove_friend'
                            : profile.friendship === 'incoming'
                            ? 'social_decline'
                            : 'social_cancel_request'
                        )}
                      </button>
                    )}
                  </div>
                )}
              </section>
            )}
            {(tab === 'feed' || (tab === 'profile' && target === self)) && (
              <section className="ps-compose">
                <div className="ps-compose-person">
                  {me && <Portrait person={me} onClick={() => view(self)} />}
                  <span aria-hidden="true">☺</span>
                </div>
                <textarea
                  ref={composeRef}
                  aria-label={String(t('social_post_text'))}
                  placeholder={String(t('social_post_text'))}
                  maxLength={2000}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
                {photo && (
                  <div>
                    <img className="ps-post-image" src={photo} alt="" />
                    <button disabled={busy} onClick={() => setPhoto('')}>
                      {t('social_remove_image')}
                    </button>
                  </div>
                )}
                <div className="ps-actions">
                  <input
                    ref={fileRef}
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) run(async () => setPhoto(await prepareSocialImage(f)));
                      e.target.value = '';
                    }}
                  />
                  <button disabled={busy} onClick={() => fileRef.current?.click()}>
                    {t('social_add_image')}
                  </button>
                  <select aria-label={String(t('social_feeling'))} value={feeling} onChange={(e) => setFeeling(e.target.value)}>
                    <option value="">{t('social_feeling')}</option>
                    {feelings.map((f) => (
                      <option key={f} value={f}>
                        {emoji[f]} {t(`social_feeling_${f}`)}
                      </option>
                    ))}
                  </select>
                  <button
                    className="primary"
                    disabled={busy || !(body.trim() || photo || feeling)}
                    onClick={() =>
                      run(async () => {
                        await request('post', { body, image: photo, feeling });
                        setBody('');
                        setPhoto('');
                        setFeeling('');
                        refresh();
                      })
                    }>
                    {t('social_publish')}
                  </button>
                </div>
              </section>
            )}
            {tab === 'people' && (
              <section>
                <h3>{t('social_people')}</h3>
                <input
                  aria-label={String(t('social_search'))}
                  placeholder={String(t('social_search'))}
                  value={query}
                  maxLength={64}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {people.map((p) => (
                  <Portrait key={p.id} person={p} onClick={() => view(p.id)} />
                ))}
                {!people.length && <p>{t('social_empty')}</p>}
              </section>
            )}
            {tab === 'friends' && (
              <section>
                <h3>{t('social_friends')}</h3>
                {connections.map((p) => (
                  <div key={p.id} className="ps-connection">
                    <Portrait person={p} onClick={() => view(p.id)} />
                    <small>
                      {t(p.state === 'accepted' ? 'social_friend' : p.outgoing ? 'social_request_sent' : 'social_request_received')}
                    </small>
                  </div>
                ))}
                {!connections.length && <p>{t('social_empty')}</p>}
              </section>
            )}
            {['feed', 'following', 'profile'].includes(tab) && (
              <>
                <div className="ps-feed">
                  {posts.map((post) => (
                    <PostCard key={post.post_id} post={post} self={self} view={view} onChanged={refresh} />
                  ))}
                </div>
                {!posts.length && <p className="ps-empty">{t('social_empty')}</p>}
                {more && (
                  <button
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const feed = await request<{ posts: Post[]; more: boolean }>('feed', {
                          cursor: posts[posts.length - 1]?.post_id,
                          target: tab === 'profile' ? target : undefined,
                          following: tab === 'following',
                        });
                        setPosts((p) => [...p, ...feed.posts]);
                        setMore(feed.more);
                      })
                    }>
                    {t('social_more')}
                  </button>
                )}
              </>
            )}
          </main>
          <aside className="ps-right">
            {me && (
              <section className="ps-profile-card">
                <div className="ps-cover" />
                <button className="ps-card-avatar" aria-label={String(t('social_edit_profile'))} onClick={() => view(self)}>
                  <img src={me.photo || `${process.env.REACT_APP_CDN_PATH ?? ''}icons/people/${me.avatar}.png`} alt="" />
                </button>
                <h3>{me.name}</h3>
                <p>
                  {t('Level')} {me.level}
                </p>
                <div className="ps-card-counts">
                  <button onClick={() => view(self)}>
                    <b>{me.friends}</b>
                    <span>{t('social_friend')}</span>
                  </button>
                  <button onClick={() => setTab('following')}>
                    <b>{me.following}</b>
                    <span>{t('social_following')}</span>
                  </button>
                  <div>
                    <b>{me.followers}</b>
                    <span>{t('social_followers')}</span>
                  </div>
                </div>
              </section>
            )}
            <button className="ps-discover-card" onClick={() => setTab('people')}>
              <span aria-hidden="true">♟</span>
              <b>{t('social_people')}</b>
              <small>{t('social_find_hint')}</small>
            </button>
            <button className="ps-friends-card" onClick={() => setTab('friends')}>
              <span aria-hidden="true">✉</span>
              <b>{t('social_friends')}</b>
              <small>{t('social_friends_hint')}</small>
            </button>
          </aside>
        </div>
        {(storyPhoto || selectedStory) && (
          <div className="ps-story-overlay" role="dialog" aria-modal="true" aria-label={String(t('social_story'))}>
            <section className="ps-story-dialog">
              <header>
                <b>{selectedStory?.name ?? t('social_add_story')}</b>
                <button
                  disabled={busy}
                  autoFocus
                  aria-label={String(t('Close'))}
                  onClick={() => {
                    setStoryPhoto('');
                    setSelectedStory(null);
                  }}>
                  {t('Close')}
                </button>
              </header>
              {error && (
                <p className="ps-error" role="alert">
                  {t(error)}
                </p>
              )}
              <img className="ps-story-full" src={storyPhoto || selectedStory?.image} alt={String(t('social_story_image'))} />
              {storyPhoto ? (
                <>
                  <input
                    aria-label={String(t('social_story_caption'))}
                    placeholder={String(t('social_story_caption'))}
                    maxLength={300}
                    value={storyBody}
                    onChange={(e) => setStoryBody(e.target.value)}
                  />
                  <small>{t('social_story_expiry')}</small>
                  <button
                    className="primary"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        await request('story', { image: storyPhoto, body: storyBody });
                        setStoryPhoto('');
                        setStoryBody('');
                        refresh();
                      })
                    }>
                    {t('social_publish_story')}
                  </button>
                </>
              ) : (
                <>
                  <p className="ps-body">{selectedStory?.body}</p>
                  {selectedStory?.author === self && (
                    <button
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          await request('deleteStory', { story: selectedStory.story_id });
                          setSelectedStory(null);
                          refresh();
                        })
                      }>
                      {t('social_delete')}
                    </button>
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </Panel>
  );
}
function PostCard({ post, self, view, onChanged }: { post: Post; self: number; view: (id: number) => void; onChanged: () => void }) {
  const { t } = useTranslation();
  const [mine, setMine] = useState(post.my_reaction ?? null),
    [reactions, setReactions] = useState(post.reactions ?? {});
  const [reactionMenu, setReactionMenu] = useState(false);
  useEffect(() => {
    setMine(post.my_reaction ?? null);
    setReactions(post.reactions ?? {});
  }, [post.my_reaction, post.reactions]);
  const [commentCount, setCommentCount] = useState(post.comments);
  useEffect(() => setCommentCount(post.comments), [post.comments]);
  const [open, setOpen] = useState(false),
    [comments, setComments] = useState<Comment[]>([]),
    [body, setBody] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const load = async (append = false) => {
    const rows = await request<Comment[]>('comments', {
      post: post.post_id,
      cursor: append ? comments[comments.length - 1]?.comment_id : undefined,
    });
    setComments((old) => (append ? [...old, ...rows] : rows));
  };
  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'social_error_failed');
    } finally {
      setBusy(false);
    }
  };
  const react = (reaction: string | null) =>
    run(async () => {
      const result = await request<{ mine: string | null; counts: Record<string, number> }>('react', { post: post.post_id, reaction });
      setMine(result.mine);
      setReactions(result.counts);
      setReactionMenu(false);
    });
  return (
    <article className="ps-post">
      <header>
        <Portrait person={post} onClick={() => view(post.author)} />
        <time>{new Date(Number(post.created_at)).toLocaleString()}</time>
        {post.author === self && (
          <button
            disabled={busy}
            onClick={() =>
              run(async () => {
                await request('deletePost', { post: post.post_id });
                onChanged();
              })
            }>
            {t('social_delete')}
          </button>
        )}
      </header>
      {post.feeling && (
        <p className="ps-feeling">
          {emoji[post.feeling]} {t(`social_feeling_${post.feeling}`, { defaultValue: post.feeling })}
        </p>
      )}
      <p className="ps-body">{post.body}</p>
      {post.image && <img className="ps-post-image" src={post.image} alt={String(t('social_attached_image'))} loading="lazy" />}
      {Object.values(reactions).some((count) => count > 0) && (
        <div className="ps-reaction-counts" aria-live="polite">
          {Object.entries(reactions)
            .filter(([, count]) => count > 0)
            .map(([kind, count]) => (
              <span key={kind} title={String(t(`social_reaction_${kind}`))}>
                {reactionIcons[kind]} {count}
              </span>
            ))}
        </div>
      )}
      <div className="ps-post-actions">
        <div
          className="ps-reaction-control"
          onMouseEnter={() => setReactionMenu(true)}
          onMouseLeave={() => setReactionMenu(false)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) setReactionMenu(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setReactionMenu(false);
          }}>
          {reactionMenu && (
            <div className="ps-reaction-picker" role="group" aria-label={String(t('social_choose_reaction'))}>
              {Object.entries(reactionIcons).map(([kind, icon]) => (
                <button
                  key={kind}
                  disabled={busy}
                  title={String(t(`social_reaction_${kind}`))}
                  aria-label={String(t(`social_reaction_${kind}`))}
                  aria-pressed={mine === kind}
                  onClick={() => react(mine === kind ? null : kind)}>
                  {icon}
                </button>
              ))}
            </div>
          )}
          <button disabled={busy} aria-pressed={!!mine} className={mine ? 'ps-reacted' : ''} onClick={() => react(mine ? null : 'like')}>
            {reactionIcons[mine ?? 'like']} {t(`social_reaction_${mine ?? 'like'}`)}
          </button>
        </div>
        <button
          disabled={busy}
          onClick={() =>
            run(async () => {
              if (!open) await load();
              setOpen(!open);
            })
          }>
          {t('social_comments', { count: commentCount })}
        </button>
      </div>
      {error && (
        <p role="alert" className="ps-error">
          {t(error)}
        </p>
      )}
      {open && (
        <div className="ps-comments">
          {comments.map((c) => (
            <div className="ps-comment" key={c.comment_id}>
              <Portrait person={c} onClick={() => view(c.id)} />
              <p className="ps-body">{c.body}</p>
              {c.id === self && (
                <button
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await request('deleteComment', { comment: c.comment_id });
                      setCommentCount((count) => Math.max(0, count - 1));
                      await load();
                    })
                  }>
                  {t('social_delete')}
                </button>
              )}
            </div>
          ))}
          {comments.length > 0 && comments.length % 30 === 0 && (
            <button disabled={busy} onClick={() => run(() => load(true))}>
              {t('social_more')}
            </button>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              run(async () => {
                await request('comment', { post: post.post_id, body });
                setCommentCount((count) => count + 1);
                setBody('');
                await load();
              });
            }}>
            <input
              aria-label={String(t('social_comment_text'))}
              placeholder={String(t('social_comment_text'))}
              maxLength={1000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <button disabled={busy || !body.trim()}>{t('social_send')}</button>
          </form>
        </div>
      )}
    </article>
  );
}
